/**
 * The **rail-page codec + source-of-truth hook** — the URL ↔ app-rail seam (`decisions/0203`
 * point 6). The rail shows one page at a time, and the URL holds that page and nothing else:
 * `/compendium/mechs/frame/mf_everest`, `/pilot/edit`, `/squad/<pilotId>`; `/` is the closed rail.
 * The stage is server state (`session.tableScene`) and never rides the URL. Each page push is a
 * real history entry, so Back walks pages.
 *
 * Split deliberately, like the desktop codec it replaces (`decisions/0019`, superseded):
 *  - the **codecs** ({@link decodeRailPath}/{@link encodeRailPath}, each app's tail codec, the
 *    scene rule {@link railAfterSceneChange}, the desktop-era redirect {@link legacyDesktopPath})
 *    are pure and React-free, proven in `railState.test.ts`;
 *  - {@link useRail} is the thin React layer over TanStack Router's splat route (`/$`).
 *
 * Ids are path segments as-is: every id the rail carries (uuids, compendium ids, codex slugs)
 * is slash- and space-free, so nothing is escaped.
 */
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import type { TableScene } from "@lancer-vtt/domain";
import type { CompendiumType, Ref, SectionId } from "../compendium/compendiumData";
import { sectionOf } from "../compendium/compendiumView";

// ---- the pages -------------------------------------------------------------------------

/** PILOT's and MECH's VIEW / EDIT mode: the sheet, or the editor. */
export type PilotMode = "view" | "edit";

/** MECH's modes: PILOT's pair plus the hangar, the pilot's mechs as tiles with ＋ NEW MECH (`decisions/0206` point 3). */
export type MechMode = PilotMode | "hangar";

/**
 * COMPENDIUM's durable navigation: the `section/category/selected` triple — where in the archive
 * you are. The ephemeral remainder (back-stack, facets, filter, search text) stays in the archive's
 * reducer and is NOT URL-bearing.
 */
export type CompendiumPage = {
  section: SectionId;
  category: CompendiumType;
  /** The open record, when one is selected; `null` is "browsing the category list." */
  selected: Ref | null;
};

/** CODEX's panes: ROLODEX only since CHART's removal (`decisions/0203` point 10). */
export type CodexTab = "rolodex";

/**
 * One rail page. An absent id means the page's own default: PILOT/MECH without an id
 * are the viewer's selected pilot (a player) or a "pick one from ROSTER" note (the GM); SQUAD
 * without an id is the board; CODEX without an id is the ROLODEX list; CHEATSHEET
 * without an id is the role's first page.
 */
export type RailPage =
  | { app: "pilot"; pilotId?: string; mode: PilotMode }
  | { app: "mech"; mechId?: string; mode?: MechMode }
  | { app: "squad"; pilotId?: string }
  | { app: "compendium"; page: CompendiumPage }
  | { app: "codex"; tab: CodexTab; id?: string }
  | { app: "cheatsheet"; pageId?: string }
  | { app: "dice" }
  | { app: "encounters"; view: "list" | "new" }
  | { app: "roster" }
  | { app: "settings" }
  | { app: "director" };

export type RailApp = RailPage["app"];

/**
 * Narrow pages sit beside the stage; wide pages cover it up to COMMS (`decisions/0203` point 5).
 * A medium page covers the stage's left side at its content's reading width, for a page whose
 * content would leave a blank band in a wide pane.
 */
export const RAIL_WIDTH: Record<RailApp, "narrow" | "medium" | "wide"> = {
  pilot: "wide",
  mech: "wide",
  compendium: "wide",
  codex: "wide",
  cheatsheet: "medium",
  squad: "wide",
  dice: "medium",
  encounters: "narrow",
  roster: "narrow",
  settings: "narrow",
  director: "narrow",
};

/**
 * Each role's strip, top to bottom: the role's own work, then {@link REFERENCE_APPS} under a
 * divider. The GM's work leads with DIRECTOR, the page a GM uses most at the table. Every entry is
 * app-wide; the scene's own panels (UNITS, Map setup) live on the board's tool rail
 * (`decisions/0203` point 5, amended 2026-10-04). The GM has no DICE page: the GM rolls from
 * COMMS's dice composer (`CommsDice.tsx`, `dice-rework` 1.2). The GM's SETTINGS (seats and the
 * room lock) sits in the strip footer, not this list.
 */
export const RAIL_APPS_BY_ROLE: Record<"gm" | "player", RailApp[]> = {
  gm: ["director", "encounters", "roster", "compendium", "codex", "cheatsheet"],
  player: ["pilot", "mech", "squad", "dice", "compendium", "codex", "cheatsheet"],
};

/** The strip's REFERENCE group, the same for both roles. */
export const REFERENCE_APPS: readonly RailApp[] = ["compendium", "codex", "cheatsheet"];

/**
 * The pages each role may open: its strip, plus the GM's pages with no strip entry. ROSTER and
 * CODEX open a pilot's PILOT and MECH pages; the strip footer opens SETTINGS.
 */
const RAIL_PAGES_BY_ROLE: Record<"gm" | "player", RailApp[]> = {
  gm: [...RAIL_APPS_BY_ROLE.gm, "settings", "pilot", "mech"],
  player: RAIL_APPS_BY_ROLE.player,
};

export function railPageAllowed(page: RailPage, role: "gm" | "player"): boolean {
  return RAIL_PAGES_BY_ROLE[role].includes(page.app);
}

// ---- COMPENDIUM's tail codec -----------------------------------------------------------

const SECTION_IDS: SectionId[] = ["MECHS", "PILOTS", "NPCS", "GLOSSARY"];
const isSection = (v: string): v is SectionId => (SECTION_IDS as string[]).includes(v);

/**
 * COMPENDIUM's tail, the segments after `compendium/`:
 *   `section/category`               → browsing a category list
 *   `section/category/<id>`          → a record whose type IS the category (the common case)
 *   `section/category/<type>/<id>`   → a record of another type (a cross-link's landing)
 * Lower-cased section for readability, decoded case-insensitively. A malformed tail decodes to as
 * much as parses and never throws.
 */
export const COMPENDIUM_CODEC = {
  decode(segs: string[]): CompendiumPage {
    const sectionRaw = (segs[0] ?? "").toUpperCase();
    const section: SectionId = isSection(sectionRaw) ? sectionRaw : "MECHS";
    const category = (segs[1] ?? "frame") as CompendiumType;
    let selected: Ref | null = null;
    if (segs.length === 3) selected = { type: category, id: segs[2]! };
    else if (segs.length >= 4) selected = { type: segs[2] as CompendiumType, id: segs.slice(3).join("/") };
    return { section, category, selected };
  },
  encode(page: CompendiumPage): string[] {
    const head = [page.section.toLowerCase(), page.category];
    if (page.selected === null) return head;
    if (page.selected.type === page.category) return [...head, page.selected.id];
    return [...head, page.selected.type, page.selected.id];
  },
};

/** A selection's section/category are implied by its type — mirror the archive reducer's `select`. */
export function compendiumPageForRef(ref: Ref): CompendiumPage {
  return { section: sectionOf(ref.type), category: ref.type, selected: ref };
}

export const DEFAULT_COMPENDIUM_PAGE: CompendiumPage = { section: "MECHS", category: "frame", selected: null };

// ---- the whole path --------------------------------------------------------------------

/** Decode a rail path (`/compendium/mechs/frame`, with or without slashes at either end). */
export function decodeRailPath(path: string): RailPage | null {
  const [app, ...rest] = path.split("/").filter((s) => s.length > 0);
  const first = rest[0];
  switch (app) {
    case "pilot": {
      if (first === undefined || first === "view") return { app, mode: "view" };
      if (first === "edit") return { app, mode: "edit" };
      return { app, pilotId: first, mode: rest[1] === "edit" ? "edit" : "view" };
    }
    case "mech": {
      if (first === undefined) return { app };
      if (first === "hangar") return { app, mode: "hangar" };
      const mode = rest[1] === "edit" || rest[1] === "hangar" ? rest[1] : undefined;
      return mode ? { app, mechId: first, mode } : { app, mechId: first };
    }
    case "squad":
    // TEAM's path before the rename (`decisions/0205` point 1).
    case "team":
      return first === undefined ? { app: "squad" } : { app: "squad", pilotId: first };
    case "compendium":
      return { app, page: COMPENDIUM_CODEC.decode(rest) };
    case "codex": {
      // An unknown tab segment (a removed CHART link included) degrades to the registry list.
      const id = first === "rolodex" ? rest[1] : undefined;
      return id === undefined ? { app, tab: "rolodex" } : { app, tab: "rolodex", id };
    }
    case "cheatsheet":
      return first === undefined ? { app } : { app, pageId: first };
    case "encounters":
      return { app, view: first === "new" ? "new" : "list" };
    case "dice":
    case "roster":
    case "settings":
    case "director":
      return { app };
    default:
      return null;
  }
}

/** Encode a rail page as its path, without the leading slash; the closed rail is `""`. */
export function encodeRailPath(page: RailPage | null): string {
  if (page === null) return "";
  const segs: (string | undefined)[] = [page.app];
  switch (page.app) {
    case "pilot":
      segs.push(page.pilotId, page.mode === "edit" ? "edit" : undefined);
      break;
    case "mech":
      segs.push(page.mechId, page.mode === "hangar" || (page.mechId !== undefined && page.mode === "edit") ? page.mode : undefined);
      break;
    case "squad":
      segs.push(page.pilotId);
      break;
    case "compendium":
      segs.push(...COMPENDIUM_CODEC.encode(page.page));
      break;
    case "codex":
      if (page.id !== undefined) segs.push(page.tab, page.id);
      break;
    case "cheatsheet":
      segs.push(page.pageId);
      break;
    case "encounters":
      if (page.view === "new") segs.push("new");
      break;
  }
  return segs.filter((s): s is string => s !== undefined).join("/");
}

// ---- the scene rule and the strip's state ----------------------------------------------

const sceneKey = (scene: TableScene) => (scene.kind === "encounter" ? `encounter:${scene.encounterId}` : "narrative");

/**
 * Combat clears the screen (`decisions/0203` point 5): when the table moves into an encounter, an
 * open page closes so the map is visible. Acts only on a scene CHANGE (`prev` undefined is this
 * client's first sight, which never closes a page the URL already holds), so a page the viewer
 * opens afterwards is never fought. It never opens a page: out of combat the expanded strip is
 * the open-by-default state ({@link stripExpanded}).
 */
export function railAfterSceneChange(args: { prev: TableScene | undefined; next: TableScene; page: RailPage | null }): { close: true } | undefined {
  const { prev, next, page } = args;
  if (prev === undefined || sceneKey(prev) === sceneKey(next)) return undefined;
  return next.kind === "encounter" && page !== null ? { close: true } : undefined;
}

/**
 * The strip expands (icon, title and subtitle per app) when nothing needs the screen: no page is
 * open and the stage is not showing a map. Whenever a page is open or the stage shows an encounter
 * it collapses to icons.
 */
export function stripExpanded(args: { page: RailPage | null; scene: TableScene }): boolean {
  return args.page === null && args.scene.kind === "narrative";
}

// ---- the redirects ------------------------------------------------------------------------

/**
 * A retired page's path → the page that replaced it, or undefined. FORGE stopped being a page
 * (`decisions/0204`): `/forge/<id>` lands on that pilot's PILOT page, where the FORGE key is.
 * SLOTS was renamed USERS, and USERS moved into SETTINGS: `/slots` and `/users` land on `/settings`.
 */
export function legacyRailPath(path: string): string | undefined {
  const [app, id] = path.split("/").filter((s) => s.length > 0);
  if (app === "slots" || app === "users") return encodeRailPath({ app: "settings" });
  if (app !== "forge") return undefined;
  return encodeRailPath(id === undefined ? { app: "pilot", mode: "view" } : { app: "pilot", pilotId: id, mode: "view" });
}

/**
 * A `/?<app>=<sub-route>` link from the free-window desktop (`decisions/0019`) → the rail path it
 * meant, or undefined when it carries none. The desktop could hold several apps at once; the rail
 * holds one, so the first present param wins, in the order a deep link most likely aimed at.
 */
export function legacyDesktopPath(search: Record<string, unknown>): string | undefined {
  const str = (v: unknown): string | undefined => (typeof v === "string" && v.length > 0 ? v : undefined);
  const forge = str(search.forge);
  if (forge) return encodeRailPath({ app: "pilot", pilotId: forge, mode: "view" });
  const mech = str(search.mech);
  if (mech) return encodeRailPath({ app: "mech", mechId: mech });
  const pilot = str(search.pilot);
  if (pilot) return encodeRailPath({ app: "pilot", mode: pilot === "edit" ? "edit" : "view" });
  const team = str(search.team);
  if (team) return encodeRailPath(team === "__team_list__" ? { app: "squad" } : { app: "squad", pilotId: team });
  const compendium = str(search.compendium);
  if (compendium) return encodeRailPath({ app: "compendium", page: COMPENDIUM_CODEC.decode(compendium.split("/").filter((s) => s.length > 0)) });
  const rolodex = str(search.rolodex);
  if (rolodex) return encodeRailPath(rolodex === "__rolodex_list__" ? { app: "codex", tab: "rolodex" } : { app: "codex", tab: "rolodex", id: rolodex });
  const cheatsheet = str(search.cheatsheet);
  if (cheatsheet) return encodeRailPath({ app: "cheatsheet", pageId: cheatsheet });
  return undefined;
}

// ---- the source-of-truth hook ------------------------------------------------------------

export type Rail = {
  /** The page the URL holds, or null for the closed rail. */
  page: RailPage | null;
  /** Push a page (a real history entry, so Back returns to the page before); resolves once it lands. */
  open: (page: RailPage, opts?: { replace?: boolean }) => Promise<void>;
  /** Close the rail (`/`). */
  close: (opts?: { replace?: boolean }) => void;
};

/** The rail page off the URL, plus navigations that write it. */
export function useRail(): Rail {
  const navigate = useNavigate();
  const splat = useParams({ strict: false })._splat;
  const page = useMemo(() => decodeRailPath(splat ?? ""), [splat]);
  const open = useCallback(
    (next: RailPage, opts?: { replace?: boolean }) => navigate({ to: "/$", params: { _splat: encodeRailPath(next) }, replace: opts?.replace }),
    [navigate],
  );
  const close = useCallback(
    (opts?: { replace?: boolean }) => {
      void navigate({ to: "/", replace: opts?.replace });
    },
    [navigate],
  );
  return { page, open, close };
}
